import React, { useEffect, useState, useMemo, useRef } from "react";
import * as Yup from "yup";
import FormBuilder from "../../../../../../../shared/components/form-builder/form-builder";
import { useApi } from "../../../../../../../shared/hooks/useApi";
import { useParams } from "react-router-dom";
import { TCAppConfig } from "../../../../../../../shared/app-configs/tc-app-configs";
import { Alert, Box, styled, Typography } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { FormConfigProps } from "../../../../../../../shared/components/form-builder/form-builder.model";
import { useFormActions } from "../../../../../../../shared/hooks/form-builder.hooks";
import { standardCodeFromValue, hasCertTypeToken } from "../incoming-details-apis";
import {
  ensureExternalIncomingCertificateTypes,
  extractRmdfPercentage,
  filterIncomingCertificateTypes,
  getIncomingTcSourceVersion,
  unwrapRmdfLookupItems,
} from "./incoming-rmdf.utils";
import { buildIncomingSourceLookupKey } from "./incoming-source-state.utils";

interface IncomingTcInfoProps {
  formRef: React.RefObject<any>;
  componentData: any;
  setComponentData: (data: any) => void;
}

interface Option {
  value: string;
  label: string;
  tcId?: string;
  atcId?: string;
  mdfId?: string;
  fpfId?: string;
  sourceVersion?: string;
}

type IncomingSourceType = "V3" | "V4" | "RMDF" | "FPF" | "ATU" | "GOTS" | "";
type IncomingV4IssuedFlag = "Y" | "N";

type IncomingRawMaterialSource = {
  tcProductCategoryCode?: string;
  tcProductDetailCode?: string;
  rawMaterialCode?: string;
  rawMaterialPercentage?: number;
  standard?: string;
  isCertified?: string;
  tcProductNetShippingWeight?: number;
  tcProductSupplementaryWeight?: number;
  tcProductAdditivesWeight?: number;
};

const DISALLOWED_INCOMING_PLACEHOLDERS = ["na", "n/a", "not available", "not applicable", "null"];
const hasValidIncomingText = (value?: string | null) => {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized !== "" && !DISALLOWED_INCOMING_PLACEHOLDERS.includes(normalized);
};
const normalizeIncomingV4IssuedFlag = (value: any): IncomingV4IssuedFlag =>
  String(value || "").trim().toUpperCase() === "Y" ? "Y" : "N";
const extractLeadingCode = (value: any): string => {
  const text = String(value || "").trim();
  if (!text) return "";
  const directMatch = text.match(/\b(?:PC|PD|RM)\d{4}\b/i);
  if (directMatch) return directMatch[0].toUpperCase();
  return text.split(" - ")[0].trim();
};
const toNumberOrZero = (value: any): number => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const extractPercentage = (item: any, rawMaterialCode?: string): number =>
  extractRmdfPercentage(item, rawMaterialCode);

const addUniqueRawMaterial = (
  target: IncomingRawMaterialSource[],
  row: IncomingRawMaterialSource
) => {
  const key = [
    row.tcProductCategoryCode || "",
    row.tcProductDetailCode || "",
    row.rawMaterialCode || "",
    Number(row.rawMaterialPercentage || 0),
    row.standard || "",
  ].join("|");
  const exists = target.some((item) => [
    item.tcProductCategoryCode || "",
    item.tcProductDetailCode || "",
    item.rawMaterialCode || "",
    Number(item.rawMaterialPercentage || 0),
    item.standard || "",
  ].join("|") === key);
  if (!exists) target.push(row);
};

const normalizeStandardValues = (value: any): string[] => {
  const values = Array.isArray(value) ? value : value ? [value] : [];
  const flattened = typeof (Array.prototype as any).flat === "function"
    ? (values as any).flat(Infinity)
    : values.reduce((acc: any[], cur: any) => acc.concat(Array.isArray(cur) ? cur : [cur]), []);
  return Array.from(new Set(flattened.map((item: any) => String(item || "").trim()).filter(Boolean)));
};

const unwrapIncomingProducts = (payload: any): any[] => {
  if (!payload) return [];
  let data = payload;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return [];
    }
  }
  if (data?.response !== undefined) return unwrapIncomingProducts(data.response);
  const rows = Array.isArray(data) ? data : [data];
  return rows.flatMap((row: any) => {
    const value = row?.trackitPortalData ?? row;
    return Array.isArray(value) ? value : [value];
  }).filter(Boolean);
};

const buildIncomingArticleMaps = (products: any[]) => {
  const standardsByArticle: { [productNo: string]: string[] } = {};
  const rawMaterialsByArticle: { [productNo: string]: IncomingRawMaterialSource[] } = {};
  const maxWeightByArticle: { [productNo: string]: number } = {};
  const sourceByArticle: { [productNo: string]: IncomingRawMaterialSource } = {};
  const standards: string[] = [];

  unwrapIncomingProducts(products).forEach((product: any) => {
    const articleNo = String(product?.tcProductArticleNo || product?.tcProductNo || product?.atcProductNo || "").trim();
    if (!articleNo) return;

    const netWeight = toNumberOrZero(product?.tcProductNetShippingWeight ?? product?.netShippingWeight);
    const supplementaryWeight = toNumberOrZero(product?.tcProductSupplementaryWeight ?? product?.supplementaryWeight);
    const additivesWeight = toNumberOrZero(product?.tcProductAdditivesWeight ?? product?.additivesWeight);
    // Claims first: the backend validates submitted standards against the
    // source product's certified-weight claims, so the UI must show the same
    // set. Codes are normalised via the shared label-to-code mapper (naive
    // slice(0,3) turned "Materials Matter..." into "Mat").
    const claimStandards = normalizeStandardValues(
      (product?.tcProductCertifiedWeightInformation || [])
        .map((item: any) => standardCodeFromValue(item?.tcProductClaimsCategory))
    );
    const productStandards = claimStandards.length > 0
      ? claimStandards
      : normalizeStandardValues(
          product?.standards ||
          product?.tcProductStandard ||
          product?.tcProductStandardInformation?.map((item: any) => standardCodeFromValue(item?.tcProductStandardLabelGrade))
        );

    if (productStandards.length > 0) {
      standardsByArticle[articleNo] = Array.from(new Set([...(standardsByArticle[articleNo] || []), ...productStandards]));
      standards.push(...productStandards);
    }
    if (netWeight > 0) {
      maxWeightByArticle[articleNo] = Math.max(maxWeightByArticle[articleNo] || 0, netWeight);
    }

    const rawMaterials = Array.isArray(product?.tcProductRawMaterialInformation)
      ? product.tcProductRawMaterialInformation
      : Array.isArray(product?.rawMaterialInformation)
      ? product.rawMaterialInformation
      : [];

    rawMaterials.forEach((rm: any) => {
      const rawMaterialCode = extractLeadingCode(
        rm?.tcProductRawMaterialCode ||
        rm?.rawMaterialCode ||
        rm?.tcProductRawMaterialCodeStandardLabel ||
        rm?.tcProductIncomingCertifiedRawMaterialCode
      );
      const sourceRow: IncomingRawMaterialSource = {
        tcProductCategoryCode: extractLeadingCode(product?.tcProductCategoryCode || product?.atcProductCategoryCode),
        tcProductDetailCode: extractLeadingCode(product?.tcProductDetailCode || product?.atcProductDetailCode),
        rawMaterialCode,
        rawMaterialPercentage: extractPercentage({
          rawMaterialPercentage: rm?.tcProductRawMaterialPercentage ?? rm?.rawMaterialPercentage ?? rm?.rawMaterialCompostion,
        }),
        standard: productStandards[0] || "",
        isCertified: rm?.isCertified ?? rm?.is_certified ?? "",
        tcProductNetShippingWeight: netWeight,
        tcProductSupplementaryWeight: supplementaryWeight,
        tcProductAdditivesWeight: additivesWeight,
      };
      rawMaterialsByArticle[articleNo] = rawMaterialsByArticle[articleNo] || [];
      addUniqueRawMaterial(rawMaterialsByArticle[articleNo], sourceRow);
      sourceByArticle[articleNo] = sourceByArticle[articleNo] || sourceRow;
    });

    if (!rawMaterialsByArticle[articleNo]?.length) {
      const sourceRow: IncomingRawMaterialSource = {
        tcProductCategoryCode: extractLeadingCode(product?.tcProductCategoryCode || product?.atcProductCategoryCode),
        tcProductDetailCode: extractLeadingCode(product?.tcProductDetailCode || product?.atcProductDetailCode),
        rawMaterialCode: "",
        rawMaterialPercentage: 100,
        standard: productStandards[0] || "",
        tcProductNetShippingWeight: netWeight,
        tcProductSupplementaryWeight: supplementaryWeight,
        tcProductAdditivesWeight: additivesWeight,
      };
      sourceByArticle[articleNo] = sourceByArticle[articleNo] || sourceRow;
    }
  });

  return {
    standards: Array.from(new Set(standards)),
    standardsByArticle,
    sourceByArticle,
    rawMaterialsByArticle,
    maxWeightByArticle,
  };
};

export type SourceArticleData = {
  maps: ReturnType<typeof buildIncomingArticleMaps>;
  standards: string[];
  productOptions: Option[];
};

// Fetches every product from the canonical incoming-source entity. This is the
// only product-data path used by the stepper; certificate lookup endpoints are
// responsible only for resolving the current valid canonical source ID.
export const fetchSourceArticleMaps = async (
  executeGet: (url: string, params?: Record<string, any>) => Promise<any>,
  params: { type: string; tcId?: string; atcId?: string }
): Promise<SourceArticleData | null> => {
  const type = String(params.type || "").toUpperCase();
  const sourceId = params.tcId || params.atcId;
  if (!sourceId || !type) return null;

  const response = await executeGet(TCAppConfig.GET_INCOMING_TC_INFO, {
    incomingTcNoTcId: sourceId,
    incomingTcType: type,
  });
  const products = unwrapIncomingProducts(response?.data?.response ?? response?.data);
  const maps = buildIncomingArticleMaps(products);
  const productOptions: Option[] = products
    .map((product: any) => {
      const article = String(product?.tcProductArticleNo || "").trim();
      return { value: article, label: article };
    })
    .filter(
      (item: Option, index: number, self: Option[]) =>
        Boolean(item.value) && index === self.findIndex((other) => other.value === item.value)
    );

  return { maps, standards: maps.standards, productOptions };
};

// Dropdown options are static reference data; cache them at module level so a
// remount (e.g. stepping Back) renders the selected values immediately
// instead of showing blank selects until the refetch resolves.
let cachedLicensingCodes: Option[] | null = null;
let cachedCertificateTypes: Option[] | null = null;

const IncomingTcInfo: React.FC<IncomingTcInfoProps> = ({
  formRef,
  componentData,
  setComponentData,
}) => {
  const { getCurrentFormValues: getTransactionDetailsFormValues } =
    useFormActions("Transaction-Details");

  const transactionDetails = getTransactionDetailsFormValues() || {};
  const isReturn = transactionDetails.tcIsReturn === "Yes";
  const returnTcId = transactionDetails.returnTcId;
  const returnTcNo = transactionDetails.returnTcNo;

  const returnTcSourceVersion = transactionDetails.returnTcSourceVersion || "";
  const isV4 = String(returnTcSourceVersion).toLowerCase().includes("v4") || String(returnTcSourceVersion).toLowerCase().includes("atu");
  const extractedLicensing = returnTcNo ? (returnTcNo.split("-")[0] || "") : "";
  const licenseValue = extractedLicensing ? (extractedLicensing.startsWith("CB-") ? extractedLicensing : `CB-${extractedLicensing}`) : "";

  const [productOptions, setProductOptions] = useState<Option[]>([]);
  const [incomingProductOptions, setIncomingProductOptions] = useState<Option[]>([]);
  const [incomingStandards, setIncomingStandards] = useState<string[]>([]);
  const [disableIncomingProduct, setDisableIncomingProduct] = useState(true);
  const [loadingStandards, setLoadingStandards] = useState(false);
  const [activeSearchField, setActiveSearchField] = useState<"tc" | "atc" | "rmdf" | "fpf" | null>(isReturn ? "tc" : null);
  const [licensingCodes, setLicensingCodes] = useState<Option[]>(cachedLicensingCodes || []);
  const [selectedCertType, setSelectedCertType] = useState<string>(isReturn ? "TC" : "");
  const [selectedLicensingCode, setSelectedLicensingCode] = useState<string>(isReturn ? licenseValue : "");
  const [productIncomingTcNo, setProductIncomingTcNo] = useState<string>(isReturn ? returnTcNo : "");
  const [incomingV4IssuedState, setIncomingV4IssuedState] = useState<IncomingV4IssuedFlag>(
    isReturn ? (isV4 ? "Y" : "N") : normalizeIncomingV4IssuedFlag(componentData.tcProductIncomingTcV4Issued)
  );

  const [certificateTypeOptions, setCertificateTypeOptions] = useState<Option[]>(cachedCertificateTypes || []);
  const [sourceLookupError, setSourceLookupError] = useState("");

  useEffect(() => {
    if (isReturn && returnTcId && returnTcNo) {
      const returnTcSourceVersion = transactionDetails.returnTcSourceVersion || "";
      const isV4 = String(returnTcSourceVersion).toLowerCase().includes("v4") || String(returnTcSourceVersion).toLowerCase().includes("atu");
      const returnSourceType: IncomingSourceType = isV4 ? "V4" : "V3";
      const extractedLicensing = returnTcNo.split("-")[0] || "";
      const licenseValue = extractedLicensing.startsWith("CB-") ? extractedLicensing : `CB-${extractedLicensing}`;

      if (formRef?.current) {
        formRef.current.setFieldValue("tcProductIncomingTcV4Issued", isV4 ? "Y" : "N");
        formRef.current.setFieldValue("certificateType", "TC");
        formRef.current.setFieldValue("licensingCode", licenseValue);
        formRef.current.setFieldValue("searchCertificateNumber", returnTcNo);
        formRef.current.setFieldValue("tcIncomingTcId", returnTcId);
        formRef.current.setFieldValue("tcIncomingTCNo", returnTcNo);
      }

      if (
        componentData.tcIncomingTcId !== returnTcId ||
        componentData.tcIncomingTCNo !== returnTcNo ||
        componentData.licensingCode !== licenseValue ||
        componentData.incomingTcLicensingCode !== extractedLicensing
      ) {
        setComponentData({
          ...componentData,
          tcProductIncomingTcV4Issued: isV4 ? "Y" : "N",
          certificateType: "TC",
          licensingCode: licenseValue,
          incomingTcLicensingCode: extractedLicensing,
          tcIncomingTCNo: returnTcNo,
          tcIncomingTcId: returnTcId,
          tcIncomingType: returnSourceType,
        });
      }

      const alreadyFetched = componentData.tcIncomingTcId === returnTcId &&
        Object.keys(componentData.incomingProductRawMaterialsByArticle || {}).length > 0;

      if (!alreadyFetched) {
        void fetchIncomingSourceDetails(returnTcId, returnSourceType);
      }
    }
  }, [isReturn, returnTcId, returnTcNo, formRef, componentData]);

  // Guards against async races: results from a superseded search/selection are
  // dropped instead of clobbering newer state (values "flickering" / being
  // cleared after landing on step 2 came from exactly these late resolves).
  const searchSeqRef = useRef(0);
  // Number the selection handler already fetched for; the hydration effect
  // must not re-run the whole search cascade for it.
  const lastSearchedRef = useRef<string>("");

  const { TE_ID, formtype } = useParams();
  const { executeGet } = useApi();
  const theme = useTheme();
  const hasSubmitTc: boolean = formtype === "submited-tc";

  const certificateTypes = useMemo(
    () => filterIncomingCertificateTypes(certificateTypeOptions, incomingV4IssuedState),
    [certificateTypeOptions, incomingV4IssuedState]
  );

  const fetchProductOptions = async () => {
    try {
      const response = await executeGet(TCAppConfig.GET_LIST_PRODUCT_INFO_V4, { tcId: TE_ID });
      const options: Option[] =
        (response?.data?.response || []).map((item: any) => ({
          value: item.tcProductArticleNo,
          label: item.tcProductArticleNo,
        })) || [];

      // remove duplicates
      const uniqueOptions = options.filter(
        (item, index, self) => index === self.findIndex((t) => t.value === item.value)
      );

      setProductOptions(uniqueOptions);
    } catch (err) {
      console.error("Failed to fetch product options:", err);
    }
  };

  const normalizeStandards = (arr: any): string[] => {
    if (!Array.isArray(arr)) return [];
    // Flatten nested arrays and convert values to strings
    if (typeof (Array.prototype as any).flat === "function") {
      return (arr as any).flat(Infinity).filter((v: any) => v !== null && v !== undefined).map(String);
    }
    return (arr as any).reduce((acc: any[], cur: any) => acc.concat(Array.isArray(cur) ? cur : [cur]), []).filter((v: any) => v !== null && v !== undefined).map(String);
  };

  const fetchIncomingSourceDetails = async (
    sourceId: string,
    sourceType: IncomingSourceType
  ) => {
    if (!sourceId || !sourceType) return;
    setLoadingStandards(true);
    const seq = searchSeqRef.current;

    try {
      const sourceData = await fetchSourceArticleMaps(executeGet, {
        type: sourceType,
        tcId: sourceId,
      });
      if (seq !== searchSeqRef.current || !sourceData) return;

      setSourceLookupError("");
      setIncomingStandards(sourceData.standards);
      formRef?.current?.setFieldValue("tcIncomingTCStandard", sourceData.standards);
      setIncomingProductOptions(sourceData.productOptions);
      setDisableIncomingProduct(sourceData.productOptions.length === 0);
      // Only the keys this fetch owns — the parent merges over its current
      // state, so nothing changed while the request was in flight is lost.
      setComponentData({
        tcIncomingType: sourceType,
        tcProductIncomingTcV4Issued: sourceType === "V4" ? "Y" : "N",
        tcIncomingTCStandard: sourceData.standards,
        incomingProductStandardsByArticle: sourceData.maps.standardsByArticle,
        incomingProductSourceByArticle: sourceData.maps.sourceByArticle,
        incomingProductRawMaterialsByArticle: sourceData.maps.rawMaterialsByArticle,
        incomingProductMaxWeightByArticle: sourceData.maps.maxWeightByArticle,
      });
      if (sourceData.productOptions.length === 0) {
        setSourceLookupError(
          `The current ${sourceType} source was found, but its Incoming TC information contains no product articles.`
        );
      }
    } catch (err) {
      console.error(`Failed to fetch ${sourceType} incoming source details:`, err);
      if (seq === searchSeqRef.current) {
        setIncomingStandards([]);
        setIncomingProductOptions([]);
        setDisableIncomingProduct(true);
        setComponentData({
          tcIncomingProductNo: [],
          tcIncomingTCStandard: [],
          incomingProductStandardsByArticle: {},
          incomingProductSourceByArticle: {},
          incomingProductRawMaterialsByArticle: {},
          incomingProductMaxWeightByArticle: {},
          __replaceMaps: true,
        });
        setSourceLookupError(
          `Unable to load Incoming TC information for the selected ${sourceType} source.`
        );
      }
    } finally {
      if (seq === searchSeqRef.current) {
        setLoadingStandards(false);
      }
    }
  };

  const fetchIncomingProductArticles = (
    sourceId: string,
    sourceType: IncomingSourceType
  ) => fetchIncomingSourceDetails(sourceId, sourceType);

  const fetchIncomingATCDetails = (
    sourceId: string,
    sourceType: IncomingSourceType
  ) => fetchIncomingSourceDetails(sourceId, sourceType);


  const getTCNoforSearch = async (
    value: string,
    offset: number,
    type: string,
    cbLicensingCode?: string
  ): Promise<Option[]> => {
    if(value === 'na') return [];
    try {
      const expectedSourceVersion = getIncomingTcSourceVersion(incomingV4IssuedState);
      const queryParams: Record<string, any> = {
        tcNo: type === "query" ? value : "",
        offset,
        name: type === "name" ? value : "",
        tcCbLicensingCode: cbLicensingCode,
        sourceVersion: expectedSourceVersion,
      };

      const response: any = await executeGet(TCAppConfig.GET_TC_NO_INPUT_REF, queryParams);
      // The function app wraps rows in {response: [...]}; the container proxy
      // may return the bare array. Accept both shapes.
      const rows = Array.isArray(response?.data)
        ? response.data
        : response?.data?.response || [];
      return rows
        .map((item: any) => ({
          label: item.tcNo,
          value: item.tcNo,
          tcId: item.tcId,
          sourceVersion: item.sourceVersion || item.tcSourceVersion || item.version || "",
        }))
        .filter((item: Option) =>
          String(item.sourceVersion || "").trim().toUpperCase() === expectedSourceVersion
        );
    } catch (e) {
      console.error(`Error fetching Input TC No: ${e}`);
      return [];
    }
  };

  const getInputATCNoForSearch = async (
    value: string,
    offset: number,
    type: string,
    cbLicensingCode?: string,
    requestedSourceType?: "ATU" | "GOTS"
  ): Promise<Option[]> => {
    try {
      const queryParams: Record<string, any> = {
        atcNo: type === "query" ? value : "",
        offset,
        name: type === "name" ? value : "",
        atcCbLicensingCode: cbLicensingCode,
      };

      const response: any = await executeGet(TCAppConfig.GET_ATC_NO, queryParams);
      const allResults = response.data?.response || [];
      
      // Type tokens may sit anywhere in the number (e.g. ATU_CUC_123 or
      // CUC_ATU_123), so match separator-split tokens, not just the prefix.
      const hasToken = (no: any, token: string) => {
        try {
          return String(no || '').toUpperCase().split(/[^A-Z0-9]+/).includes(token);
        } catch {
          return false;
        }
      };
      const sourceTypeToMatch = requestedSourceType || selectedCertType;
      const filteredResults = allResults.filter((item: any) => {
        if (sourceTypeToMatch === 'GOTS') {
          return hasToken(item.atcNo, 'GOTS');
        } else if (sourceTypeToMatch === 'ATU') {
          return hasToken(item.atcNo, 'ATU');
        }
        return true;
      });

      return filteredResults.map((item: any) => ({
        label: item.atcNo,
        value: item.atcNo,
        atcId: item.atcId,
      })) || [];
    } catch (e) {
      console.error(`Error fetching Input ATC No: ${e}`);
      return [];
    }
  };

  const getRMDFforSearch = async (
    value: string,
    offset: number,
    type: string,
    cbLicensingCode?: string
  ): Promise<Option[]> => {
    try {
      const queryParams: Record<string, any> = {
        rmdfNo: type === "query" ? value : "",
        offset,
        name: type === "name" ? value : "",
        tcCbLicensingCode: cbLicensingCode,
      };

      const response: any = await executeGet(TCAppConfig.GET_RMDF_SEARCH, queryParams);
      const items = unwrapRmdfLookupItems(response?.data);

      return items.map((item: any) => ({
        label: item.tcEtcMdfNumber || item.rmdfMdfNumber,
        value: item.tcEtcMdfNumber || item.rmdfMdfNumber,
        mdfId: item.mdfId,
      } as Option)) || [];
    } catch (e) {
      console.error(`Error fetching Input RMDF No: ${e}`);
      return [];
    }
  };

  const getFPFforSearch = async (
    value: string,
    offset: number,
    type: string,
    cbLicensingCode?: string
  ): Promise<Option[]> => {
    try {
      const queryParams: Record<string, any> = {
        fpfNo: type === "query" ? value : "",
        offset,
        tcCbLicensingCode: cbLicensingCode,
      };

      const response: any = await executeGet(TCAppConfig.GET_FPF_SEARCH, queryParams);
      const items = response?.data?.response || response?.data || [];

      return items.map((group: any) => {
        const fpfNo = group.tcEtcMdfNumber || group.fpfNumber || group.fpfNo || group.fpfNoId;
        return {
          label: fpfNo,
          value: fpfNo,
          fpfId: group.fpfId,
        } as Option;
      }) || [];
    } catch (e) {
      console.error(`Error fetching FPF No: ${e}`);
      return [];
    }
  };

  const handleCertificateNumberChange = (selectedOption: Option | null) => {
    const value = selectedOption?.value || null;
    setSourceLookupError("");

    // Preserve the licensing code from both state and parent data
    const licensingCodeToPreserve = selectedLicensingCode || 
      (componentData.incomingTcLicensingCode ? 
        (componentData.incomingTcLicensingCode.startsWith("CB-") ? 
          componentData.incomingTcLicensingCode.replace("CB-", "") : 
          componentData.incomingTcLicensingCode) 
        : "");
    
    if (selectedCertType === 'TC') {
      formRef?.current?.setFieldValue("tcIncomingTCNo", value);
      setProductIncomingTcNo(value || "");

      if (value) {
        resetIncomingFields();
        setSourceLookupError("");
        setActiveSearchField("tc");
        const seq = ++searchSeqRef.current;
        lastSearchedRef.current = buildIncomingSourceLookupKey("TC", selectedLicensingCode, value);
        const selectedPromise = selectedOption?.tcId
          ? Promise.resolve([selectedOption])
          : getTCNoforSearch(value, 0, "query", selectedLicensingCode);
        selectedPromise.then((res) => {
          if (seq !== searchSeqRef.current) return;
          const selected = res.find((r) => r.value === value);
          if (selected?.tcId) {
            setSourceLookupError("");
            formRef?.current?.setFieldValue("tcIncomingTcId", selected.tcId);
            // Restore licensing code to form field after reset
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            const incomingType: IncomingSourceType = String(selected.sourceVersion || "").toLowerCase().includes("v3") ? "V3" : "V4";
            // Propagate updated ID to parent component, clearing old product selections
            setComponentData({
              tcIncomingTCNo: value,
              tcIncomingTcId: selected.tcId,
              tcIncomingType: incomingType,
              tcProductIncomingTcV4Issued: incomingV4IssuedState,
              tcIncomingAtcId: "",
              tcIncomingLedgerTransactionId: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              incomingProductStandardsByArticle: {},
              incomingProductSourceByArticle: {},
              incomingProductRawMaterialsByArticle: {},
              incomingProductMaxWeightByArticle: {},
              incomingTcLicensingCode: licensingCodeToPreserve
            });
            fetchIncomingProductArticles(selected.tcId, incomingType);
          } else {
            clearResolvedIncomingSource("TC", value);
            // Restore licensing code to form field
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            setComponentData({
              tcIncomingTCNo: value,
              tcIncomingTcId: "",
              tcIncomingType: "",
              tcProductIncomingTcV4Issued: incomingV4IssuedState,
              tcIncomingAtcId: "",
              tcIncomingLedgerTransactionId: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              incomingProductStandardsByArticle: {},
              incomingProductSourceByArticle: {},
              incomingProductRawMaterialsByArticle: {},
              incomingProductMaxWeightByArticle: {},
              incomingTcLicensingCode: licensingCodeToPreserve
            });
          }
        });
      } else {
        setActiveSearchField(null);
        resetIncomingFields();
        searchSeqRef.current += 1;
        lastSearchedRef.current = "";
        formRef?.current?.setFieldValue("tcIncomingTcId", "");
        // Restore licensing code to form field
        if (licensingCodeToPreserve) {
          const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
          formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
          setSelectedLicensingCode(codeWithPrefix);
        }
        setComponentData({
          tcIncomingTCNo: "",
          tcIncomingTcId: "",
          tcIncomingType: "",
          tcProductIncomingTcV4Issued: incomingV4IssuedState,
          tcIncomingAtcId: "",
          tcIncomingLedgerTransactionId: "",
          tcIncomingProductNo: [],
          tcIncomingTCStandard: [],
          incomingProductStandardsByArticle: {},
          incomingProductSourceByArticle: {},
          incomingProductRawMaterialsByArticle: {},
          incomingProductMaxWeightByArticle: {},
          incomingTcLicensingCode: licensingCodeToPreserve
        });
      }
    } else if (selectedCertType === 'RMDF') {
      formRef?.current?.setFieldValue("tcIncomingRMDFNo", value);
      setProductIncomingTcNo(value || "");
      
      if (value) {
        resetIncomingFields();
        setSourceLookupError("");
        setActiveSearchField("rmdf");
        const seq = ++searchSeqRef.current;
        lastSearchedRef.current = buildIncomingSourceLookupKey("RMDF", selectedLicensingCode, value);
        const selectedPromise = selectedOption?.mdfId
          ? Promise.resolve([selectedOption])
          : getRMDFforSearch(value, 0, "query", selectedLicensingCode);
        selectedPromise.then((res) => {
          if (seq !== searchSeqRef.current) return;
          const selected = res.find((r) => r.value === value);
          const sourceId = selected?.mdfId || "";
          if (selected && sourceId) {
            setSourceLookupError("");
            formRef?.current?.setFieldValue("tcIncomingTcId", sourceId);
            formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
            // Restore licensing code to form field after reset
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }

            setComponentData({
              tcIncomingRMDFNo: value,
              tcIncomingTcId: sourceId,
              tcIncomingLedgerTransactionId: "",
              tcIncomingType: "RMDF",
              tcProductIncomingTcV4Issued: "N",
              tcIncomingAtcId: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              incomingProductStandardsByArticle: {},
              incomingProductSourceByArticle: {},
              incomingProductRawMaterialsByArticle: {},
              incomingProductMaxWeightByArticle: {},
              incomingTcLicensingCode: licensingCodeToPreserve
            });
            fetchIncomingProductArticles(sourceId, "RMDF");
          } else {
            clearResolvedIncomingSource("RMDF", value);
            // Restore licensing code to form field
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            setComponentData({
              tcIncomingRMDFNo: value,
              tcIncomingLedgerTransactionId: "",
              tcIncomingType: "RMDF",
              tcProductIncomingTcV4Issued: "N",
              tcIncomingTcId: "",
              tcIncomingAtcId: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              incomingProductStandardsByArticle: {},
              incomingProductSourceByArticle: {},
              incomingProductRawMaterialsByArticle: {},
              incomingProductMaxWeightByArticle: {},
              incomingTcLicensingCode: licensingCodeToPreserve
            });
          }
        });
      } else {
        setActiveSearchField(null);
        resetIncomingFields();
        searchSeqRef.current += 1;
        lastSearchedRef.current = "";
        formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
        // Restore licensing code to form field
        if (licensingCodeToPreserve) {
          const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
          formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
          setSelectedLicensingCode(codeWithPrefix);
        }
        setComponentData({
          tcIncomingRMDFNo: "",
          tcIncomingLedgerTransactionId: "",
          tcIncomingType: "",
          tcProductIncomingTcV4Issued: "N",
          tcIncomingTcId: "",
          tcIncomingAtcId: "",
          tcIncomingProductNo: [],
          tcIncomingTCStandard: [],
          incomingProductStandardsByArticle: {},
          incomingProductSourceByArticle: {},
          incomingProductRawMaterialsByArticle: {},
          incomingProductMaxWeightByArticle: {},
          incomingTcLicensingCode: licensingCodeToPreserve
        });
      }
    } else if (selectedCertType === 'FPF') {
      formRef?.current?.setFieldValue("tcIncomingFPFNo", value);
      setProductIncomingTcNo(value || "");

      if (value) {
        resetIncomingFields();
        setSourceLookupError("");
        setActiveSearchField("fpf");
        const seq = ++searchSeqRef.current;
        lastSearchedRef.current = buildIncomingSourceLookupKey("FPF", selectedLicensingCode, value);
        const selectedPromise = selectedOption?.fpfId
          ? Promise.resolve([selectedOption])
          : getFPFforSearch(value, 0, "query", selectedLicensingCode);
        selectedPromise.then((res) => {
          if (seq !== searchSeqRef.current) return;
          const selected = res.find((r) => r.value === value);
          const sourceId = selected?.fpfId || "";
          if (selected && sourceId) {
            setSourceLookupError("");
            formRef?.current?.setFieldValue("tcIncomingTcId", sourceId);
            formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }

            setComponentData({
              tcIncomingFPFNo: value,
              tcIncomingTcId: sourceId,
              tcIncomingLedgerTransactionId: "",
              tcIncomingType: "FPF",
              tcProductIncomingTcV4Issued: "N",
              tcIncomingAtcId: "",
              tcIncomingTCNo: "",
              tcIncomingATCNo: "",
              tcIncomingRMDFNo: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              tcIncomingFpfId: selected.fpfId || "",
              incomingProductStandardsByArticle: {},
              incomingProductSourceByArticle: {},
              incomingProductRawMaterialsByArticle: {},
              incomingProductMaxWeightByArticle: {},
              incomingTcLicensingCode: licensingCodeToPreserve
            });
            fetchIncomingProductArticles(sourceId, "FPF");
          } else {
            clearResolvedIncomingSource("FPF", value);
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            setComponentData({
              tcIncomingFPFNo: value,
              tcIncomingLedgerTransactionId: "",
              tcIncomingType: "FPF",
              tcProductIncomingTcV4Issued: "N",
              tcIncomingTcId: "",
              tcIncomingAtcId: "",
              tcIncomingTCNo: "",
              tcIncomingATCNo: "",
              tcIncomingRMDFNo: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              tcIncomingFpfId: "",
              incomingProductStandardsByArticle: {},
              incomingProductSourceByArticle: {},
              incomingProductRawMaterialsByArticle: {},
              incomingProductMaxWeightByArticle: {},
              incomingTcLicensingCode: licensingCodeToPreserve
            });
          }
        });
      } else {
        setActiveSearchField(null);
        resetIncomingFields();
        searchSeqRef.current += 1;
        lastSearchedRef.current = "";
        formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
        if (licensingCodeToPreserve) {
          const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
          formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
          setSelectedLicensingCode(codeWithPrefix);
        }
        setComponentData({
          tcIncomingFPFNo: "",
          tcIncomingLedgerTransactionId: "",
          tcIncomingType: "",
          tcProductIncomingTcV4Issued: "N",
          tcIncomingTcId: "",
          tcIncomingAtcId: "",
          tcIncomingTCNo: "",
          tcIncomingATCNo: "",
          tcIncomingRMDFNo: "",
          tcIncomingProductNo: [],
          tcIncomingTCStandard: [],
          tcIncomingFpfId: "",
          incomingProductStandardsByArticle: {},
          incomingProductSourceByArticle: {},
          incomingProductRawMaterialsByArticle: {},
          incomingProductMaxWeightByArticle: {},
          incomingTcLicensingCode: licensingCodeToPreserve
        });
      }
    } else if (selectedCertType === 'GOTS' || selectedCertType === 'ATU') {
      formRef?.current?.setFieldValue("tcIncomingATCNo", value);
      setProductIncomingTcNo(value || "");
      
      if (value) {
        resetIncomingFields();
        setSourceLookupError("");
        setActiveSearchField("atc");
        const seq = ++searchSeqRef.current;
        lastSearchedRef.current = buildIncomingSourceLookupKey(selectedCertType, selectedLicensingCode, value);
        const selectedPromise = selectedOption?.atcId
          ? Promise.resolve([selectedOption])
          : getInputATCNoForSearch(
              value,
              0,
              "query",
              selectedLicensingCode,
              selectedCertType === "GOTS" ? "GOTS" : "ATU"
            );
        selectedPromise.then((res) => {
          if (seq !== searchSeqRef.current) return;
          const selected = res.find((r) => r.value === value);
          if (selected?.atcId) {
            setSourceLookupError("");
            formRef?.current?.setFieldValue("tcIncomingTcId", selected.atcId);
            formRef?.current?.setFieldValue("tcIncomingAtcId", selected.atcId);
            // Restore licensing code to form field after reset
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            const sourceType: IncomingSourceType = selectedCertType === "GOTS" ? "GOTS" : "ATU";
            // Propagate updated ID to parent component, clearing old product selections
            setComponentData({
              tcIncomingATCNo: value,
              tcIncomingTcId: selected.atcId,
              tcIncomingAtcId: selected.atcId,
              tcIncomingType: sourceType,
              tcProductIncomingTcV4Issued: "N",
              tcIncomingLedgerTransactionId: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              tcIncomingFpfId: "",
              incomingProductStandardsByArticle: {},
              incomingProductSourceByArticle: {},
              incomingProductRawMaterialsByArticle: {},
              incomingProductMaxWeightByArticle: {},
              incomingTcLicensingCode: licensingCodeToPreserve
            });
            fetchIncomingATCDetails(selected.atcId, sourceType);
          } else {
            clearResolvedIncomingSource(selectedCertType === "GOTS" ? "GOTS" : "ATU", value);
            // Restore licensing code to form field
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            setComponentData({
              tcIncomingATCNo: value,
              tcIncomingAtcId: "",
              tcIncomingType: selectedCertType === "GOTS" ? "GOTS" : "ATU",
              tcProductIncomingTcV4Issued: "N",
              tcIncomingTcId: "",
              tcIncomingLedgerTransactionId: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              tcIncomingFpfId: "",
              incomingProductStandardsByArticle: {},
              incomingProductSourceByArticle: {},
              incomingProductRawMaterialsByArticle: {},
              incomingProductMaxWeightByArticle: {},
              incomingTcLicensingCode: licensingCodeToPreserve
            });
          }
        });
      } else {
        setActiveSearchField(null);
        resetIncomingFields();
        searchSeqRef.current += 1;
        lastSearchedRef.current = "";
        formRef?.current?.setFieldValue("tcIncomingAtcId", "");
        // Restore licensing code to form field
        if (licensingCodeToPreserve) {
          const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
          formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
          setSelectedLicensingCode(codeWithPrefix);
        }
        setComponentData({
          tcIncomingATCNo: "",
          tcIncomingAtcId: "",
          tcIncomingType: "",
          tcProductIncomingTcV4Issued: "N",
          tcIncomingTcId: "",
          tcIncomingLedgerTransactionId: "",
          tcIncomingProductNo: [],
          tcIncomingTCStandard: [],
          tcIncomingFpfId: "",
          incomingProductStandardsByArticle: {},
          incomingProductSourceByArticle: {},
          incomingProductRawMaterialsByArticle: {},
          incomingProductMaxWeightByArticle: {},
          incomingTcLicensingCode: licensingCodeToPreserve
        });
      }
    }
  };

  const resetIncomingFields = () => {
    setDisableIncomingProduct(true);
    setIncomingProductOptions([]);
    setIncomingStandards([]);
    formRef?.current?.setFieldValue("tcIncomingTCStandard", []);
    formRef?.current?.setFieldValue("tcIncomingProductNo", []);
    formRef?.current?.setFieldValue("tcIncomingTcId", "");
    formRef?.current?.setFieldValue("tcIncomingAtcId", "");
    formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
    formRef?.current?.setFieldValue("tcIncomingFpfId", "");
  };

  const clearResolvedIncomingSource = (
    sourceType: "TC" | "RMDF" | "FPF" | "ATU" | "GOTS",
    sourceNumber: string
  ) => {
    resetIncomingFields();
    setComponentData({
      tcIncomingTcId: "",
      tcIncomingAtcId: "",
      tcIncomingLedgerTransactionId: "",
      tcIncomingFpfId: "",
      tcIncomingType: sourceType === "TC" ? "" : sourceType,
      tcProductIncomingTcV4Issued:
        sourceType === "TC" ? incomingV4IssuedState : "N",
      tcIncomingProductNo: [],
      tcIncomingTCStandard: [],
      incomingProductStandardsByArticle: {},
      incomingProductSourceByArticle: {},
      incomingProductRawMaterialsByArticle: {},
      incomingProductMaxWeightByArticle: {},
      __replaceMaps: true,
    });
    setSourceLookupError(
      `No current valid ${sourceType} source was found for ${sourceNumber} and the selected CB licensing code.`
    );
  };

  // On load/reload, resolve the number again even when the submitted JSON
  // already contains an ID. External submissions may omit the ID, and an old
  // saved ID may point at a superseded version. Lookup endpoints select the
  // latest valid source; product data then comes only from incoming-tc-info.
  useEffect(() => {
    if (isReturn) return;
    const incomingTCNo = componentData.tcIncomingTCNo;
    const incomingATCNo = componentData.tcIncomingATCNo;
    const incomingRMDFNo = componentData.tcIncomingRMDFNo;
    const incomingFPFNo = componentData.tcIncomingFPFNo;
    const licenseCodeRaw = componentData.incomingTcLicensingCode || componentData.licensingCode || "";

    const licenseValueForForm = licenseCodeRaw
      ? licenseCodeRaw.startsWith("CB-")
        ? licenseCodeRaw
        : `CB-${licenseCodeRaw}`
      : "";

    if (licenseValueForForm) {
      setSelectedLicensingCode(licenseValueForForm);
      try {
        formRef?.current?.setFieldValue("licensingCode", licenseValueForForm);
      } catch (e) {
        // ignore if formRef not yet available
      }
    }

    const atcSourceType: "ATU" | "GOTS" = hasCertTypeToken(incomingATCNo, "GOTS") ? "GOTS" : "ATU";
    const sourceTypeForLookup: "TC" | "RMDF" | "FPF" | "ATU" | "GOTS" = incomingTCNo
      ? "TC"
      : incomingRMDFNo
      ? "RMDF"
      : incomingFPFNo
      ? "FPF"
      : atcSourceType;
    const currentNo = incomingTCNo || incomingRMDFNo || incomingFPFNo || incomingATCNo || "";
    const currentLookupKey = buildIncomingSourceLookupKey(
      sourceTypeForLookup,
      licenseValueForForm,
      currentNo
    );
    const alreadyHandled = Boolean(currentLookupKey) && lastSearchedRef.current === currentLookupKey;

    const restoreSelections = () => {
      if (Array.isArray(componentData.tcIncomingTCStandard) && componentData.tcIncomingTCStandard.length > 0) {
        const normalized = normalizeStandards(componentData.tcIncomingTCStandard);
        setIncomingStandards(normalized);
        formRef?.current?.setFieldValue("tcIncomingTCStandard", normalized);
      }
      if (Array.isArray(componentData.tcIncomingProductNo) && componentData.tcIncomingProductNo.length > 0) {
        formRef?.current?.setFieldValue("tcIncomingProductNo", componentData.tcIncomingProductNo);
      }
    };

    restoreSelections();

    if (currentNo && !licenseValueForForm) {
      clearResolvedIncomingSource(sourceTypeForLookup, currentNo);
      return;
    }

    if (incomingTCNo) {
      setSelectedCertType('TC');
      formRef?.current?.setFieldValue("certificateType", 'TC');
      setActiveSearchField("tc");
      setProductIncomingTcNo(incomingTCNo);

      if (!alreadyHandled && licenseValueForForm) {
        resetIncomingFields();
        setSourceLookupError("");
        lastSearchedRef.current = currentLookupKey;
        const seq = ++searchSeqRef.current;
        getTCNoforSearch(incomingTCNo, 0, "query", licenseValueForForm).then((res) => {
          if (seq !== searchSeqRef.current) return;
          const selected = res.find((r) => r.value === incomingTCNo);
          if (!selected?.tcId) {
            clearResolvedIncomingSource("TC", incomingTCNo);
            return;
          }
          setSourceLookupError("");
          const incomingType: IncomingSourceType = String(selected.sourceVersion || "").toLowerCase().includes("v3") ? "V3" : "V4";
          formRef?.current?.setFieldValue("tcIncomingTcId", selected.tcId);
          formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
          setComponentData({
            tcIncomingTcId: selected.tcId,
            tcIncomingType: incomingType,
            tcIncomingLedgerTransactionId: "",
            tcProductIncomingTcV4Issued: incomingV4IssuedState,
          });
          fetchIncomingProductArticles(selected.tcId, incomingType);
        });
      } else if (!alreadyHandled && componentData.tcIncomingTcId) {
        setSourceLookupError("");
        const incomingType: IncomingSourceType = String(componentData.tcIncomingType || "V4").toUpperCase() === "V3" ? "V3" : "V4";
        fetchIncomingProductArticles(componentData.tcIncomingTcId, incomingType);
      }
    } else if (incomingRMDFNo) {
      setSelectedCertType('RMDF');
      formRef?.current?.setFieldValue("certificateType", 'RMDF');
      setActiveSearchField("rmdf");
      setProductIncomingTcNo(incomingRMDFNo);

      if (!alreadyHandled && licenseValueForForm) {
        resetIncomingFields();
        setSourceLookupError("");
        lastSearchedRef.current = currentLookupKey;
        const seq = ++searchSeqRef.current;
        getRMDFforSearch(incomingRMDFNo, 0, "query", licenseValueForForm).then((res) => {
          if (seq !== searchSeqRef.current) return;
          const selected = res.find((r) => r.value === incomingRMDFNo);
          const sourceId = selected?.mdfId || "";
          if (selected && sourceId) {
            setSourceLookupError("");
            formRef?.current?.setFieldValue("tcIncomingTcId", sourceId);
            formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
            setComponentData({
              tcIncomingType: "RMDF",
              tcProductIncomingTcV4Issued: "N",
              tcIncomingTcId: sourceId,
              tcIncomingLedgerTransactionId: "",
            });
            fetchIncomingProductArticles(sourceId, "RMDF");
          } else {
            clearResolvedIncomingSource("RMDF", incomingRMDFNo);
          }
        });
      } else if (!alreadyHandled && componentData.tcIncomingTcId) {
        setSourceLookupError("");
        fetchIncomingProductArticles(componentData.tcIncomingTcId, "RMDF");
      }
    } else if (incomingFPFNo) {
      setSelectedCertType('FPF');
      formRef?.current?.setFieldValue("certificateType", 'FPF');
      setActiveSearchField("fpf");
      setProductIncomingTcNo(incomingFPFNo);

      if (!alreadyHandled && licenseValueForForm) {
        resetIncomingFields();
        setSourceLookupError("");
        lastSearchedRef.current = currentLookupKey;
        const seq = ++searchSeqRef.current;
        getFPFforSearch(incomingFPFNo, 0, "query", licenseValueForForm).then((res) => {
          if (seq !== searchSeqRef.current) return;
          const selected = res.find((r) => r.value === incomingFPFNo);
          const sourceId = selected?.fpfId || "";
          if (selected && sourceId) {
            setSourceLookupError("");
            formRef?.current?.setFieldValue("tcIncomingTcId", sourceId);
            formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
            setComponentData({
              tcIncomingType: "FPF",
              tcProductIncomingTcV4Issued: "N",
              tcIncomingTcId: sourceId,
              tcIncomingLedgerTransactionId: "",
              tcIncomingFpfId: selected.fpfId || componentData.tcIncomingFpfId || "",
            });
            fetchIncomingProductArticles(sourceId, "FPF");
          } else {
            clearResolvedIncomingSource("FPF", incomingFPFNo);
          }
        });
      } else if (!alreadyHandled && componentData.tcIncomingTcId) {
        setSourceLookupError("");
        fetchIncomingProductArticles(componentData.tcIncomingTcId, "FPF");
      }
    } else if (incomingATCNo) {
      const certType = atcSourceType;

      setSelectedCertType(certType);
      formRef?.current?.setFieldValue("certificateType", certType);
      setActiveSearchField("atc");
      setProductIncomingTcNo(incomingATCNo);

      if (!alreadyHandled && licenseValueForForm) {
        resetIncomingFields();
        setSourceLookupError("");
        lastSearchedRef.current = currentLookupKey;
        const seq = ++searchSeqRef.current;
        const sourceType: IncomingSourceType = certType === "GOTS" ? "GOTS" : "ATU";
        getInputATCNoForSearch(
          incomingATCNo,
          0,
          "query",
          licenseValueForForm,
          certType
        ).then((res) => {
          if (seq !== searchSeqRef.current) return;
          const selected = res.find((r) => r.value === incomingATCNo);
          if (!selected?.atcId) {
            clearResolvedIncomingSource(certType, incomingATCNo);
            return;
          }
          setSourceLookupError("");
          formRef?.current?.setFieldValue("tcIncomingTcId", selected.atcId);
          formRef?.current?.setFieldValue("tcIncomingAtcId", selected.atcId);
          formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
          setComponentData({
            tcIncomingTcId: selected.atcId,
            tcIncomingAtcId: selected.atcId,
            tcIncomingType: sourceType,
            tcIncomingLedgerTransactionId: "",
            tcProductIncomingTcV4Issued: "N",
          });
          fetchIncomingATCDetails(selected.atcId, sourceType);
        });
      } else if (!alreadyHandled) {
        const sourceType: IncomingSourceType = certType === "GOTS" ? "GOTS" : "ATU";
        const existingId = componentData.tcIncomingTcId || componentData.tcIncomingAtcId;
        if (existingId) {
          setSourceLookupError("");
          fetchIncomingATCDetails(existingId, sourceType);
        }
      }
    } else {
      setActiveSearchField(null);
      resetIncomingFields();
    }

  }, [componentData.tcIncomingTCNo, componentData.tcIncomingATCNo, componentData.tcIncomingRMDFNo, componentData.tcIncomingFPFNo, componentData.incomingTcLicensingCode, componentData.licensingCode]);

  const StyledScrollBox = styled(Box)(({ theme }) => ({
    borderRadius: "12px",
    border: "1px solid #ccc",
    backgroundColor: "#FFFFFF",
    padding: theme.spacing(1),
    height: "110px",
    overflowY: "auto",
    color: "#1C1C1C",
    fontSize: "0.875rem", 
    lineHeight: 1.4,
    paddingRight: theme.spacing(0.5),
  }));

  useEffect(() => {
    if (TE_ID) {
      fetchProductOptions();
    }
  }, [TE_ID]);

  const getCBLicensingCodes = async () => {
    if (cachedLicensingCodes && cachedLicensingCodes.length > 0) {
      setLicensingCodes(cachedLicensingCodes);
      return;
    }
    try {
      const result: any = await dtrackItService.getCBLicensingCodes();
      const cbLicensing = result.data.map((t: any) => {
        const code = t.code_1.replace(/^CB-/, '');
        return {
          value: t.code_1, 
          label: code, 
        };
      });
      cachedLicensingCodes = cbLicensing;
      setLicensingCodes(cbLicensing);
    } catch (error) {
      console.error('Error fetching licensing codes:', error);
    }
  }; 

  const getCertificateTypeOptions = async () => {
    if (cachedCertificateTypes && cachedCertificateTypes.length > 0) {
      setCertificateTypeOptions(cachedCertificateTypes);
      return;
    }
    try {
      const result: any = await dtrackItService.getIncomingCertificateTypes();
      const referenceOptions = (result?.data || []).map((item: any) => ({
        value: item.code_1,
        label: item.description_1,
      }));
      const options = ensureExternalIncomingCertificateTypes(referenceOptions);
      cachedCertificateTypes = options;
      setCertificateTypeOptions(options);
    } catch (error) {
      console.error("Error fetching certificate type options:", error);
    }
  };

  useEffect(() => {
    getCBLicensingCodes();
    getCertificateTypeOptions();
  }, []);

  useEffect(() => {
    if (isReturn) return;
    const issuedFlag = normalizeIncomingV4IssuedFlag(
      componentData.tcProductIncomingTcV4Issued
    );
    setIncomingV4IssuedState(issuedFlag);
    formRef?.current?.setFieldValue("tcProductIncomingTcV4Issued", issuedFlag);
  }, [componentData.tcProductIncomingTcV4Issued, formRef, isReturn]);

const validationSchema = Yup.object().shape({
  tcAssociatedProductArticleNo: Yup.string().required("Required"),
  tcProductIncomingTcV4Issued: Yup.string()
    .oneOf(["Y", "N"])
    .required("Required"),
  certificateType: Yup.string().required('Required'),
  licensingCode: Yup.string().required('Required'),
  tcIncomingTCNo: Yup.string().when('certificateType', {
    is: 'TC',
    then: (schema) =>
      schema
        .required('Required')
        .test('valid-incoming-tc-no', 'Incoming TC No cannot be blank or a placeholder value', hasValidIncomingText),
    otherwise: (schema) => schema.notRequired(),
  }),
  tcIncomingATCNo: Yup.string().when('certificateType', {
    is: (certificateType: string) => ['GOTS', 'ATU'].includes(certificateType),
    then: (schema) =>
      schema
        .required('Required')
        .test('valid-incoming-atc-no', 'Incoming ATC No cannot be blank or a placeholder value', hasValidIncomingText),
    otherwise: (schema) => schema.notRequired(),
  }),
  tcIncomingRMDFNo: Yup.string().when('certificateType', {
    is: 'RMDF',
    then: (schema) =>
      schema
        .required('Required')
        .test('valid-incoming-rmdf-no', 'Incoming RMDF No cannot be blank or a placeholder value', hasValidIncomingText),
    otherwise: (schema) => schema.notRequired(),
  }),
  tcIncomingFPFNo: Yup.string().when('certificateType', {
    is: 'FPF',
    then: (schema) =>
      schema
        .required('Required')
        .test('valid-incoming-fpf-no', 'Incoming FPF No cannot be blank or a placeholder value', hasValidIncomingText),
    otherwise: (schema) => schema.notRequired(),
  }),
  // Product article selection moved to the next step (checkbox table),
  // so it is no longer validated here.
  tcIncomingProductNo: Yup.array().notRequired(),
  tcIncomingTCStandard: Yup.array().notRequired(),
});

  const handleIncomingV4IssuedChange = (
    value: any,
    formValues?: any,
    setFieldValue?: any
  ) => {
    const issuedFlag = normalizeIncomingV4IssuedFlag(
      value?.target?.value ?? value
    );
    const certificateType = issuedFlag === "Y" ? "TC" : "";

    if (typeof setFieldValue === "function") {
      setFieldValue("tcProductIncomingTcV4Issued", issuedFlag);
      setFieldValue("certificateType", certificateType);
    } else {
      formRef?.current?.setFieldValue(
        "tcProductIncomingTcV4Issued",
        issuedFlag
      );
      formRef?.current?.setFieldValue("certificateType", certificateType);
    }

    searchSeqRef.current += 1;
    lastSearchedRef.current = "";
    setSourceLookupError("");
    setIncomingV4IssuedState(issuedFlag);
    setSelectedCertType(certificateType);
    setProductIncomingTcNo("");
    setActiveSearchField(null);
    resetIncomingFields();
    formRef?.current?.setFieldValue("searchCertificateNumber", "");
    formRef?.current?.setFieldValue("tcIncomingTCNo", "");
    formRef?.current?.setFieldValue("tcIncomingATCNo", "");
    formRef?.current?.setFieldValue("tcIncomingRMDFNo", "");
    formRef?.current?.setFieldValue("tcIncomingFPFNo", "");

    setComponentData({
      certificateType,
      tcIncomingTCNo: "",
      tcIncomingATCNo: "",
      tcIncomingRMDFNo: "",
      tcIncomingFPFNo: "",
      tcIncomingProductNo: [],
      tcIncomingTCStandard: [],
      tcIncomingTcId: "",
      tcIncomingAtcId: "",
      tcIncomingLedgerTransactionId: "",
      tcIncomingFpfId: "",
      tcIncomingType: "",
      tcProductIncomingTcV4Issued: issuedFlag,
      incomingProductStandardsByArticle: {},
      incomingProductSourceByArticle: {},
      incomingProductRawMaterialsByArticle: {},
      incomingProductMaxWeightByArticle: {},
    });
  };

  const handleCertificateTypeChange = (event: any) => {
    const value = event?.target?.value || "";
    // Invalidate any in-flight search results for the previous type/number.
    searchSeqRef.current += 1;
    lastSearchedRef.current = "";
    setSourceLookupError("");
    setSelectedCertType(value); 
    formRef?.current?.setFieldValue("certificateType", value);
  
    // Issue 3: Reset all certificate-related fields when type changes
    formRef?.current?.setFieldValue("tcIncomingTCNo", "");
    formRef?.current?.setFieldValue("tcIncomingATCNo", "");
    formRef?.current?.setFieldValue("tcIncomingRMDFNo", "");
    formRef?.current?.setFieldValue("tcIncomingFPFNo", "");
    formRef?.current?.setFieldValue("searchCertificateNumber", "");
    formRef?.current?.setFieldValue("tcIncomingTcId", "");
    formRef?.current?.setFieldValue("tcIncomingAtcId", "");
    formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
    
    setProductIncomingTcNo("");
    setActiveSearchField(null);
    resetIncomingFields();
    
    // Propagate the changes to parent.
    // certificateType MUST be persisted here: FormBuilder re-initializes from
    // componentData on every parent update, so a value living only in Formik
    // state gets wiped and the user has to select the type twice.
    setComponentData({
      certificateType: value,
      tcIncomingTCNo: "",
      tcIncomingATCNo: "",
      tcIncomingRMDFNo: "",
      tcIncomingFPFNo: "",
      tcIncomingProductNo: [],
      tcIncomingTCStandard: [],
      tcIncomingTcId: "",
      tcIncomingAtcId: "",
      tcIncomingLedgerTransactionId: "",
      tcIncomingType: "",
      tcProductIncomingTcV4Issued: incomingV4IssuedState,
      incomingProductStandardsByArticle: {},
      incomingProductSourceByArticle: {},
      incomingProductRawMaterialsByArticle: {},
      incomingProductMaxWeightByArticle: {},
    });
  };

  const handleLicensingValueChange = (event: any) => {
  const value = event?.target?.value || "";
  setSourceLookupError("");
  setSelectedLicensingCode(value);
  formRef?.current?.setFieldValue("licensingCode", value);
  // Persist into componentData so form re-initialization (and edit mode)
  // keeps the CB licensing code instead of falling back to the placeholder.
  setComponentData({
    incomingTcLicensingCode: String(value).replace(/^CB-/, ""),
  });
};

  // Accepts FormBuilder's onChange signature (value, formValues, setFieldValue)
  const handleAssociatedProductChange = (value: any, formValues?: any, setFieldValue?: any) => {
    const newVal = value?.target?.value ?? value?.value ?? value ?? "";
    try {
      if (typeof setFieldValue === 'function') {
        setFieldValue('tcAssociatedProductArticleNo', newVal);
      } else {
        formRef?.current?.setFieldValue('tcAssociatedProductArticleNo', newVal);
      }
    } catch (e) {
      // ignore if formRef not ready
    }
    // Propagate immediately to parent so parent Save sees the latest selection
    setComponentData({ ...(componentData || {}), tcAssociatedProductArticleNo: newVal });
  };

  useEffect(() => {
    const formValue = formRef?.current?.values?.licensingCode;
    if (formValue && formValue !== selectedLicensingCode) {
      setSelectedLicensingCode(formValue);
    }
  }, [formRef?.current?.values?.licensingCode]);
  
  

  const formConfig: FormConfigProps = {
    initialValues: {
      ...componentData,
      // Retained only for backend compatibility. DAT 501 removes the farm flag
      // from the UI and requires every incoming source to follow this workflow.
      isFlagSeller: "No",
      tcProductIncomingTcV4Issued: isReturn
        ? (isV4 ? "Y" : "N")
        : normalizeIncomingV4IssuedFlag(componentData.tcProductIncomingTcV4Issued),
      certificateType: isReturn
        ? "TC"
        : normalizeIncomingV4IssuedFlag(
            componentData.tcProductIncomingTcV4Issued
          ) === "Y"
        ? "TC"
        : componentData.certificateType || (
            // The number fields are already type-specific, and RMDF/FPF/ATU numbers
            // may carry the type token anywhere (RMDF_CUC_1 or CUC_RMDF_1), so key
            // off field presence and only disambiguate GOTS vs ATU by token.
            componentData.tcIncomingTCNo ? 'TC' :
            componentData.tcIncomingRMDFNo ? 'RMDF' :
            componentData.tcIncomingFPFNo ? 'FPF' :
            hasCertTypeToken(componentData.tcIncomingATCNo, 'GOTS') ? 'GOTS' :
            componentData.tcIncomingATCNo ? 'ATU' : ""
          ),
      licensingCode: isReturn
        ? licenseValue
        : (componentData.incomingTcLicensingCode ? (componentData.incomingTcLicensingCode.startsWith("CB-") ? componentData.incomingTcLicensingCode : `CB-${componentData.incomingTcLicensingCode}`) : ""), 
      tcIncomingTCStandard: normalizeStandards(componentData.tcIncomingTCStandard),
      tcIncomingProductNo: Array.isArray(componentData.tcIncomingProductNo)
        ? componentData.tcIncomingProductNo
        : componentData.tcIncomingProductNo
        ? [componentData.tcIncomingProductNo]
        : [],
      tcIncomingRMDFNo: componentData.tcIncomingRMDFNo || "",
      tcIncomingFPFNo: componentData.tcIncomingFPFNo || "",
      tcIncomingLedgerTransactionId: componentData.tcIncomingLedgerTransactionId || "",
      searchCertificateNumber: isReturn ? returnTcNo : (componentData.tcIncomingTCNo || componentData.tcIncomingATCNo || componentData.tcIncomingRMDFNo || componentData.tcIncomingFPFNo || ""),
      tcIncomingTCNo: isReturn ? returnTcNo : (componentData.tcIncomingTCNo || ""),
      tcIncomingTcId: isReturn ? returnTcId : (componentData.tcIncomingTcId || ""),
    },
    validationSchema,
    sections: [
      {
        fields: [
          {
            label: "Associated Product Article Number",
            name: "tcAssociatedProductArticleNo",
            type: "select",
            gridSize: { xs: 12, md: 6 },
            required: true,
            readOnly: hasSubmitTc,
            events: {
              onChange: handleAssociatedProductChange,
            },
            props: {
              options: productOptions,
              enableSearch: true,
              placeholder: "Select Product Article No",
              fullWidth: true,
            },
          },
          {
            label: "Incoming TC v4 Issued",
            name: "tcProductIncomingTcV4Issued",
            type: "radio",
            gridSize: { xs: 12, md: 6 },
            required: true,
            readOnly: hasSubmitTc || isReturn,
            events: {
              onChange: handleIncomingV4IssuedChange,
            },
            props: {
              options: [
                { value: "Y", label: "Yes" },
                { value: "N", label: "No" },
              ],
              disabled: hasSubmitTc || isReturn,
            },
          },
          {
            type: "component",
            label: "",
            name: "infoMessage",
            required: false,
            gridSize: { xs: 12, md: 12 },
            children: (
              <Box sx={{ color: 'text.secondary', mt: 1.5, mb: 1.5, fontSize: '0.875rem' }}>
                Choose the option and search the certificate number below for the Product Incoming TC number.
              </Box>
            ),
          },
        ],
      },
      {
        fields: [
          {
            label: "Choose Type of Certificate",
            name: "certificateType",
            type: "select",
            required: true,
            gridSize: { xs: 12, md: 4},
            props: {
              options: certificateTypes,
              onChange: handleCertificateTypeChange,
              select: true,
              native: false,
              fullWidth: true,
              placeholder: 'Select Certificate Type',
              disabled: isReturn,
            },
          },
          {
            label: "Choose CB Licensing Code",
            name: "licensingCode",
            type: "select",
            required: true,
            gridSize: { xs: 12, md: 4},
            props: {
              options: licensingCodes,
              select: true,
              native: false,
              fullWidth: true,
              enableSearch: true,
              onChange: handleLicensingValueChange,
              placeholder: 'Select Licensing Code',
              disabled: isReturn,
            },
          },
          {
            label: "Search for the Certificate Number",
            name: "searchCertificateNumber",
            type: "search",
            required: selectedCertType ? true : false,
            gridSize: { xs: 12, md: 4 },
            props: {
              onChange: handleCertificateNumberChange,
              onSearch: (query: string, offset: number) => {
                if (isReturn) {
                  return Promise.resolve([]);
                }
                if (!selectedCertType || !selectedLicensingCode) {
                  return Promise.resolve([]);
                }
                
                // Prevent API calls for "na" value (special value for farm sellers)
                if (query === 'na' || !query || query.trim() === '') {
                  return Promise.resolve([]);
                }
                
                if (selectedCertType === 'TC') {
                  return getTCNoforSearch(query, offset, "query", selectedLicensingCode);
                } else if (selectedCertType === 'RMDF') {
                  return getRMDFforSearch(query, offset, "query", selectedLicensingCode);
                } else if (selectedCertType === 'FPF') {
                  return getFPFforSearch(query, offset, "query", selectedLicensingCode);
                } else if (selectedCertType === 'GOTS' || selectedCertType === 'ATU') {
                  return getInputATCNoForSearch(query, offset, "query", selectedLicensingCode);
                }
                return Promise.resolve([]);
              },
              displayKey: "label",
              fullWidth: true,
              disabled: !selectedCertType || !selectedLicensingCode || isReturn,
              placeholder: selectedCertType === 'TC' ? 'Search TC Number' : selectedCertType === 'RMDF' ? 'Search RMDF Number' : selectedCertType === 'FPF' ? 'Search FPF Number' : 'Search ATU Number',
              value: productIncomingTcNo,
            },
          },
        ],
      },
      {
        fields: [
          {
            type: "component",
            label: "",
            name: "selectedDocumentCard",
            required: false,
            gridSize: { xs: 12, md: 12 },
            children: (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2.5,
                  mt: 2.5,
                  borderRadius: "12px",
                  border: "1.5px solid #F0CD7F",
                  backgroundColor: "#FCF8F2",
                  boxShadow: "0 2px 8px rgba(240, 205, 127, 0.15)",
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Selected Document
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: "#1C1C1C",
                      mt: 0.5,
                      fontFamily: "monospace",
                      fontSize: "1rem",
                    }}
                  >
                    {productIncomingTcNo || "No certificate selected yet"}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontStyle: "italic",
                    fontSize: "0.875rem",
                  }}
                >
                  Incoming product articles will be selected in the next step.
                </Typography>
              </Box>
            ),
          },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      const licensingRaw =
        values.licensingCode ||
        componentData.incomingTcLicensingCode ||
        componentData.licensingCode ||
        "";
      const licensingCodeOnly = String(licensingRaw).replace(/^CB-/, "");

      const payload = {
        tcAssociatedProductArticleNo: values.tcAssociatedProductArticleNo,
        certificateType: values.certificateType || componentData.certificateType || "",
        tcIncomingTCNo: values.tcIncomingTCNo || componentData.tcIncomingTCNo || "",
        tcIncomingATCNo: values.tcIncomingATCNo || componentData.tcIncomingATCNo || "",
        tcIncomingRMDFNo: values.tcIncomingRMDFNo || componentData.tcIncomingRMDFNo || "",
        tcIncomingFPFNo: values.tcIncomingFPFNo || componentData.tcIncomingFPFNo || "",
        tcIncomingProductNo:
          Array.isArray(values.tcIncomingProductNo) && values.tcIncomingProductNo.length > 0
            ? values.tcIncomingProductNo
            : componentData.tcIncomingProductNo || [],
        tcIncomingTCStandard:
          Array.isArray(values.tcIncomingTCStandard) && values.tcIncomingTCStandard.length > 0
            ? values.tcIncomingTCStandard
            : componentData.tcIncomingTCStandard || [],
        tcIncomingTcId: values.tcIncomingTcId || componentData.tcIncomingTcId || "",
        tcIncomingAtcId: values.tcIncomingAtcId || componentData.tcIncomingAtcId || "",
        tcIncomingLedgerTransactionId:
          values.tcIncomingLedgerTransactionId ||
          componentData.tcIncomingLedgerTransactionId ||
          "",
        tcIncomingFpfId: values.tcIncomingFpfId || componentData.tcIncomingFpfId || "",
        tcIncomingType: componentData.tcIncomingType || "",
        incomingTcLicensingCode: licensingCodeOnly,
        tcProductIncomingTcV4Issued: normalizeIncomingV4IssuedFlag(
          values.tcProductIncomingTcV4Issued ??
            componentData.tcProductIncomingTcV4Issued
        ),
        incomingProductStandardsByArticle: componentData.incomingProductStandardsByArticle || {},
        incomingProductSourceByArticle: componentData.incomingProductSourceByArticle || {},
        incomingProductRawMaterialsByArticle:
          componentData.incomingProductRawMaterialsByArticle || {},
        incomingProductMaxWeightByArticle: componentData.incomingProductMaxWeightByArticle || {},
        isFlagSeller: "No",
      };
      
      // Set component data without UI-only fields
      setComponentData(payload);
    },
  };

  return (
    <>
      {sourceLookupError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {sourceLookupError}
        </Alert>
      )}
      <FormBuilder formConfig={formConfig} onSubmit={formConfig.handleSubmit} formRef={formRef} />
    </>
  );
};

export default IncomingTcInfo;
